import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import avatarFallback from "@/assets/UI/avatar.png";
import walletIcon from "@/assets/UI/wallet.webp";
import coinIcon from "@/assets/UI/coin.webp";

import { formatDateTime } from "@/utils/formatDateTime";
import s from "./WalletTransactionDetail.module.scss";

const WalletTransactionDetail = () => {
  const { walletId, transactionId } = useParams();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [user, setUser] = useState(null);
  const [avatar, setAvatar] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchTransaction = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.get(
          `/v1/admin/finance/wallet-transactions/${transactionId}`,
        );
        if (!isMounted) return;
        setTransaction(res.data?.transaction ?? null);
        setWallet(res.data?.wallet ?? null);
        setUser(res.data?.user ?? null);
        setAvatar(res.data?.avatar ?? null);
      } catch (err) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load wallet transaction",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTransaction();

    return () => {
      isMounted = false;
    };
  }, [transactionId]);

  const profilePath = useMemo(() => {
    return wallet?.userId ? `/admin/members/single/${wallet.userId}` : null;
  }, [wallet?.userId]);

  const renderPrettyDateTime = (value) => {
    const dt = formatDateTime(value);
    if (!dt) return <span className={s.dash}>-</span>;

    const hasParts =
      dt.day !== undefined && dt.suffix && dt.month && dt.year !== undefined;

    return (
      <div className={s.dateTime}>
        <div className={s.date}>
          {hasParts ? (
            <>
              {dt.day}
              <sup className={s.sup}>{dt.suffix}</sup> {dt.month} {dt.year}
            </>
          ) : (
            dt.date
          )}
        </div>
        <div className={s.time}>{dt.time}</div>
      </div>
    );
  };

  const isCredit = transaction?.type === "credit";

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Wallet transaction</p>
          <h1 className={s.title}>Wallet transaction detail</h1>
          <p className={s.subtitle}>
            Review the member, wallet balance impact, and transaction metadata.
          </p>
        </div>

        <div className={s.actions}>
          <button className={s.btn} onClick={() => navigate(-1)}>
            Back
          </button>

          <Link
            className={`${s.btn} ${s.btnWallet}`}
            to={`/admin/finance/wallets/${walletId}`}
          >
            <img className={s.btnIcon} src={walletIcon} alt="" />
            <span>Wallet overview</span>
          </Link>
        </div>
      </header>

      {loading ? (
        <div className={s.stateBox}>Loading transaction…</div>
      ) : error ? (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      ) : transaction ? (
        <div className={s.grid}>
          {/* Member */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Member</h2>
              <span className={s.hint}>Linked to profile</span>
            </div>

            {profilePath ? (
              <Link to={profilePath} className={s.memberRow}>
                <span className={s.avatarRing}>
                  <img
                    src={avatar?.url || avatarFallback}
                    alt=""
                    className={s.avatarLg}
                  />
                </span>

                <span className={s.memberMeta}>
                  <span className={s.memberName}>
                    {user?.username || "Unknown user"}
                  </span>
                  <span className={s.memberSub}>Tap to open profile</span>
                </span>
              </Link>
            ) : (
              <div className={s.memberRowStatic}>
                <span className={s.avatarRing}>
                  <img
                    src={avatar?.url || avatarFallback}
                    alt=""
                    className={s.avatarLg}
                  />
                </span>

                <div className={s.memberMeta}>
                  <div className={s.memberName}>
                    {user?.username || "Unknown user"}
                  </div>
                  <div className={s.memberSub}>Profile not available</div>
                </div>
              </div>
            )}

            <div className={s.divider} />

            <p className={s.muted}>Current wallet balance</p>
            <div className={s.notyLine}>
              <span className={s.notyValue}>{wallet?.balanceNoty ?? 0}</span>
              <img className={s.coinMd} src={coinIcon} alt="" />
              <span className={s.notyUnit}>NOTY</span>
            </div>
          </section>

          {/* Transaction */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Transaction</h2>
              <span
                className={`${s.typePill} ${isCredit ? s.typeCredit : s.typeDebit}`}
              >
                {transaction.type}
              </span>
            </div>

            <div className={s.kv}>
              <div className={s.k}>
                <p className={s.muted}>Reason</p>
                <p className={s.bigText}>{transaction.reason || "-"}</p>
              </div>

              <div className={s.k}>
                <p className={s.muted}>Amount (Noty)</p>
                <div
                  className={`${s.amountLine} ${isCredit ? s.amountCredit : s.amountDebit}`}
                >
                  <span className={s.sign}>{isCredit ? "+" : "-"}</span>
                  <span className={s.amountValue}>
                    {transaction.amountNoty}
                  </span>
                  <img className={s.coinSm} src={coinIcon} alt="" />
                  <span className={s.amountUnit}>NOTY</span>
                </div>
              </div>
            </div>
          </section>

          {/* Timing */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Timing</h2>
            </div>

            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Created</p>
                {renderPrettyDateTime(transaction.createdAt)}
              </div>

              <div className={s.k}>
                <p className={s.muted}>Last updated</p>
                {renderPrettyDateTime(transaction.updatedAt)}
              </div>

              <div className={s.k}>
                <p className={s.muted}>Balance after transaction</p>
                <div className={s.notyLine}>
                  <span className={s.notyValue}>
                    {transaction.balanceAfter ?? "-"}
                  </span>
                  <img className={s.coinMd} src={coinIcon} alt="" />
                  <span className={s.notyUnit}>NOTY</span>
                </div>
              </div>
            </div>
          </section>

          {/* References */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>References</h2>
            </div>

            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Reference type</p>
                <p className={s.valueText}>
                  {transaction.referenceType || "-"}
                </p>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <div className={s.stateBox}>Transaction not found.</div>
      )}
    </div>
  );
};

export default WalletTransactionDetail;
